import classnames from 'owa-classnames';
import { logUsage } from 'owa-analytics';
import loc from 'owa-localize';
import React from 'react';

import { Link } from '@fluentui/react/lib/Link';

import { BookingPageErrorState } from '../store/schema/BookingPageErrorState';
import type ErrorPageEntrySource from '../store/schema/ErrorPageEntrySource';
import { default as bookWithMeFaqUrl } from '../utils/bookWithMeFaqUrl';
import { getMultiSessionEventImageUrl } from '../utils/getThemeImages';
import { root, isCompact, theme, text, isShowImage, subText } from './ErrorPage.scss';

import {
    bookWithMeAnonymousSharingDisabledErrorMessage,
    bookwithmeDeprecateErrorPageSubtitle,
    bookwithmeDeprecateErrorPageTitle,
    bookwithmeErrorAccessDeniedSubtitle,
    bookwithmeErrorAccessDeniedTitle,
    bookwithmeErrorUnlicensedUserSubtitle,
    bookwithmeInvalidUrlErrorText,
    bookwithmeInvalidUrlInstructions,
    bookwithmeLearnMore,
    bookwithmeLinkExpiredErrorText,
    bookwithmeLinkExpiredInstructions,
    bookwithmeNoPublicMeetingTypesErrorText,
    bookwithmeNoPublicMeetingTypesInstructions,
    bookwithmePageNotFoundText,
    bookwithmePageRefreshInstructions,
    bookwithmeSingleUseLinkAlreadyUsedErrorText,
    bookwithmeSingleUseLinkAlreadyUsedInstructions,
} from './Shared.locstring.json';
import type { ResourceId } from 'owa-localize';

export interface ErrorPageProps {
    entrySource: ErrorPageEntrySource;
    isCompactView?: boolean;
    getRequestUrl?: string;
    isExpiredPoll?: boolean;
    bookingPageErrorState?: BookingPageErrorState;
}

const ErrorPage = function ErrorPage(props: ErrorPageProps) {
    let errorText: ResourceId = '';
    let errorSubText: ResourceId = '';
    let showImage = false;
    let learnMoreLink = '';

    switch (props.entrySource) {
        case 'Organizer':
            switch (props.bookingPageErrorState) {
                // Case where anonymous sharing is disabled for the tenant
                /* to turn on:
                Set-SharingPolicy "Default Sharing Policy" -Domains @{Add="anonymous:CalendarSharingFreeBusyReviewer"}
                */
                case BookingPageErrorState.ErrorAnonymousSharingDisabled:
                    errorText = bookwithmeErrorAccessDeniedTitle;
                    errorSubText = bookWithMeAnonymousSharingDisabledErrorMessage;
                    break;
                case BookingPageErrorState.ErrorAccessDenied:
                    errorText = bookwithmeErrorAccessDeniedTitle;
                    // Note: This is the line that has "Your IT admin has not provided access to PB. Contact them ..."
                    // We may need to rephrase but it makes sense since the IT dept is the one that has disabled it, with:
                    // set-organizationconfig -EwsEnable $true -EwsApplicationAccessPolicy EnforceBlockList -EwsBlockList @{Add="MicrosoftOWSBookWithMe"}
                    errorSubText = bookwithmeErrorAccessDeniedSubtitle;
                    learnMoreLink = bookWithMeFaqUrl;
                    break;
                case BookingPageErrorState.ErrorUnlicensedUser:
                    // Personal bookings user is unlicensed
                    errorText = bookwithmeErrorAccessDeniedTitle;
                    // The user does not have a valid license (SKU): M365/O365 E3, E5, A3, A5, etc., required to access to the booking page
                    // Full list at: https://commoffertool.catalog.cp.microsoft.com/Prod/Office365/element/details/199a5c09-e0ca-4e37-8f7c-b05d533e1ea2
                    // Note: This is the line that has "The license model doesn't allow access to Personal booking, please talk to you IT administrator about getting Personal bookings enabled."
                    // We may need to rephrase but it makes sense since the IT dept is the one that can get the correct license.
                    errorSubText = bookwithmeErrorUnlicensedUserSubtitle;
                    learnMoreLink = bookWithMeFaqUrl;
                    break;
                case BookingPageErrorState.ErrorPersonalBookingsNotEnabled:
                    // Personal bookings requires the client flight 'cal-personalbookingpage' to be enabled:
                    errorText = bookwithmeErrorAccessDeniedTitle;
                    break;
            }
            break;
        case 'MainModule':
            errorText = bookwithmePageNotFoundText;
            errorSubText = bookwithmePageRefreshInstructions;
            break;
        case 'BookingPage':
            switch (props.bookingPageErrorState) {
                case BookingPageErrorState.ExpiredLink:
                    errorText = bookwithmeLinkExpiredErrorText;
                    errorSubText = bookwithmeLinkExpiredInstructions;
                    break;
                case BookingPageErrorState.SingleUseLinkAlreadyUsed:
                    errorText = bookwithmeSingleUseLinkAlreadyUsedErrorText;
                    errorSubText = bookwithmeSingleUseLinkAlreadyUsedInstructions;
                    break;
                case BookingPageErrorState.NoPublicMeetingTypes:
                    errorText = bookwithmeNoPublicMeetingTypesErrorText;
                    errorSubText = bookwithmeNoPublicMeetingTypesInstructions;
                    break;
                case BookingPageErrorState.InvalidSignedUrl:
                case BookingPageErrorState.NoBookingPageDataPayload:
                    errorText = bookwithmeInvalidUrlErrorText;
                    errorSubText = bookwithmeInvalidUrlInstructions;
                    showImage = true;
                    break;
                case BookingPageErrorState.DeprecatedPage:
                    errorText = bookwithmeDeprecateErrorPageTitle;
                    errorSubText = bookwithmeDeprecateErrorPageSubtitle;
                    showImage = true;
                    break;
            }
            break;
    }

    /* eslint-disable-next-line react-perf/jsx-no-new-object-as-prop  -- (https://aka.ms/OWALintWiki)
     * Causes extra re renders of component
     *	> JSX attribute values should not contain objects created in the same scope */
    const multiSessionImageUrl = {
        backgroundImage: `url(${getMultiSessionEventImageUrl()})`,
    };

    // Add telemtry data for error page
    const windowLocation = window.location;
    const containsGetRequestUrl = props.getRequestUrl !== undefined;
    logUsage('FTErrorPage', {
        isCompactView: props.isCompactView,
        entrySource: props.entrySource,
        urlPath: windowLocation.pathname,
        containsGetRequestUrl,
    });

    /* eslint-disable-next-line react-perf/jsx-no-new-function-as-prop  -- (https://aka.ms/OWALintWiki)
     * Baseline, please do not copy and paste this justification
     *	> JSX attribute values should not contain functions created in the same scope */
    const onClickLearnMore = () => {
        logUsage('onErrorPageLearnMoreClick', {
            isCompactView: props.isCompactView,
            entrySource: props.entrySource,
            urlPath: windowLocation.pathname,
            containsGetRequestUrl,
            errorState: props.bookingPageErrorState,
        });
    };

    return (
        <div className={classnames(root, { [isCompact]: props.isCompactView })}>
            {showImage && <div className={theme} style={multiSessionImageUrl}></div>}
            <div className={classnames(text, { [isShowImage]: showImage })}>{loc(errorText)}</div>
            <div className={classnames(subText, { [isShowImage]: showImage })}>
                {loc(errorSubText)}
            </div>
            {learnMoreLink && (
                <div className={classnames(subText, { [isShowImage]: showImage })}>
                    <Link href={learnMoreLink} onClick={onClickLearnMore}>
                        {loc(bookwithmeLearnMore)}
                    </Link>
                </div>
            )}
        </div>
    );
};

export default ErrorPage;
